import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Building2,
  FileText,
  Landmark,
  Search,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { SearchEntity, SearchResponse, SearchResult } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { T } from '@/design/tokens';
import { MODULES, canOpen } from '@/navigation';
import { useAuth } from '@/lib/auth-context';

const ENTITY_ICON: Record<SearchEntity, LucideIcon> = {
  pessoa: Users,
  usuario: User,
  'centro-custo': Wallet,
  'plano-conta': Landmark,
};

const ENTITY_GROUP: Record<SearchEntity, string> = {
  pessoa: 'Pessoas',
  usuario: 'Usuários',
  'centro-custo': 'Centros de custo',
  'plano-conta': 'Plano de contas',
};

interface NavigationHit {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  icon: LucideIcon;
}

/**
 * Busca global (⌘K / Ctrl+K).
 *
 * Duas fontes: as TELAS disponíveis (navegação por teclado, sem tirar a mão do
 * teclado) e os REGISTROS do banco. O servidor já filtra o que o perfil pode
 * ver — a busca nunca revela a existência de dado que o operador não acessa.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [wasOpen, setWasOpen] = useState(open);

  // Cada abertura começa limpa — o operador não herda a busca anterior.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTerm('');
      setResults([]);
      setError(null);
      setHighlight(0);
    }
  }

  useEffect(() => {
    // O autoFocus do React não pega em elemento montado condicionalmente.
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // Telas que este operador pode abrir.
  const screens = useMemo<NavigationHit[]>(() => {
    const items: NavigationHit[] = [];
    for (const module of MODULES) {
      if (module.to && !module.soon) {
        items.push({
          id: `nav:${module.id}`,
          title: module.label,
          subtitle: 'Ir para a tela',
          href: module.to,
          icon: module.icon,
        });
      }
      for (const child of module.children ?? []) {
        if (child.soon) continue;
        if (!canOpen(can, child.permission)) continue;
        items.push({
          id: `nav:${child.to}`,
          title: child.label,
          subtitle: `${module.label} · ir para a tela`,
          href: child.to,
          icon: module.icon,
        });
      }
    }
    return items;
  }, [can]);

  const matchedScreens = useMemo(() => {
    const query = term.trim().toLowerCase();
    if (!query) return screens.slice(0, 6);
    return screens
      .filter((screen) => `${screen.title} ${screen.subtitle}`.toLowerCase().includes(query))
      .slice(0, 5);
  }, [screens, term]);

  // Busca no servidor, com debounce e cancelamento da requisição anterior.
  useEffect(() => {
    const query = term.trim();
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      // Menos de 2 caracteres não vale uma consulta ao banco.
      if (query.length < 2) {
        setResults([]);
        setError(null);
        return;
      }
      setLoading(true);
      try {
        const response = await api.get<SearchResponse>('/search', { q: query }, controller.signal);
        setResults(response.results);
        setError(null);
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(errorMessage(cause));
      } finally {
        setLoading(false);
      }
    }, 260);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [term]);

  const flat = useMemo(
    () => [
      ...matchedScreens.map((screen) => ({ kind: 'screen' as const, item: screen })),
      ...results.map((result) => ({ kind: 'record' as const, item: result })),
    ],
    [matchedScreens, results],
  );

  const [highlightedCount, setHighlightedCount] = useState(flat.length);

  // A lista mudou de tamanho: o item destacado volta para o topo.
  if (flat.length !== highlightedCount) {
    setHighlightedCount(flat.length);
    setHighlight(0);
  }

  if (!open) return null;

  const go = (href: string) => {
    onClose();
    void navigate({ to: href });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((value) => Math.min(value + 1, flat.length - 1));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((value) => Math.max(value - 1, 0));
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = flat[highlight];
      if (target) go(target.item.href);
    }
  };

  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, result) => {
    (acc[ENTITY_GROUP[result.entity]] ??= []).push(result);
    return acc;
  }, {});

  return (
    <>
      <div
        className="overlay"
        style={{ zIndex: 70, background: 'rgba(12,43,46,.36)' }}
        onClick={onClose}
        aria-hidden
      />
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Busca global">
        <div className="cmdk__field">
          <Search size={17} color={T.inkFaint} aria-hidden />
          <input
            ref={inputRef}
            className="cmdk__input"
            placeholder="Pessoa, fornecedor, centro de custo, conta, tela…"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Buscar em tudo"
          />
          <span className="cmdk__esc">esc</span>
        </div>

        <div className="cmdk__list">
          {matchedScreens.length > 0 && (
            <>
              <div className="cmdk__group">Telas</div>
              {matchedScreens.map((screen, index) => (
                <button
                  key={screen.id}
                  type="button"
                  className={`cmdk__item${highlight === index ? ' is-highlighted' : ''}`}
                  onClick={() => go(screen.href)}
                >
                  <screen.icon size={16} color={T.mareDark} aria-hidden />
                  <div style={{ flex: 1 }}>
                    <div className="cmdk__item-title">{screen.title}</div>
                    <div className="cmdk__item-sub">{screen.subtitle}</div>
                  </div>
                  <ArrowRight size={14} color={T.inkFaint} aria-hidden />
                </button>
              ))}
            </>
          )}

          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="cmdk__group">{group}</div>
              {items.map((result) => {
                const Icon = ENTITY_ICON[result.entity] ?? Building2;
                const index = flat.findIndex((entry) => entry.item.id === result.id);
                return (
                  <button
                    key={result.id}
                    type="button"
                    className={`cmdk__item${highlight === index ? ' is-highlighted' : ''}`}
                    onClick={() => go(result.href)}
                  >
                    <Icon size={16} color={T.mareDark} aria-hidden />
                    <div style={{ flex: 1 }}>
                      <div className="cmdk__item-title">{result.title}</div>
                      {result.subtitle && <div className="cmdk__item-sub">{result.subtitle}</div>}
                    </div>
                    <ArrowRight size={14} color={T.inkFaint} aria-hidden />
                  </button>
                );
              })}
            </div>
          ))}

          {error && (
            <div className="cmdk__empty" role="alert" style={{ color: T.red }}>
              {error}
            </div>
          )}

          {!error && !loading && term.trim().length >= 2 && flat.length === 0 && (
            <div className="cmdk__empty">
              <FileText size={18} color={T.inkFaint} aria-hidden />
              <div style={{ marginTop: 8 }}>Nada encontrado para “{term.trim()}”.</div>
            </div>
          )}

          {loading && <div className="cmdk__empty">Buscando…</div>}
        </div>
      </div>
    </>
  );
}
